/**
 * MyChoresStrip — seksjonen «Mine nå» øverst på tavla (#154, steg 4b).
 *
 * Det seeren holder på med: tatte og innsendte kort, både oppgaver og lekser.
 * Før redesignet var dette en vannrett stripe over rutenettet; nå er det den
 * første av seksjonene Mine nå → Ledige → Lekser, med samme rader som resten.
 *
 * Et kort du nettopp tok, havner ikke her med en gang. Det blir stående der
 * du tok det, til du går fra tavla eller endrer utvalget («Ta flytter aldri
 * kortet», regelen står i boardFilter.ts).
 *
 * Gir ingenting (null) når seeren ikke holder på med noe. Tavla har sin egen
 * tomtilstand, og et tomt kort over den ville skjøvet oppgavene ned.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import type { BoardChoreInstance } from './useChoresQuery';

interface MyChoresStripProps {
  /**
   * The viewer's held cards. Passed in rather than fetched: the board feed
   * already carries them, so a self-contained query here would be a second
   * request for data the parent is holding.
   */
  rows: BoardChoreInstance[];
  /** Tegner ett kort; tavla velger ChoreCard eller HomeworkCard etter slag. */
  renderCard: (row: BoardChoreInstance) => ReactNode;
}

export function MyChoresStrip({ rows, renderCard }: MyChoresStripProps): ReactNode {
  const { t } = useTranslation();

  // Nothing in flight — stay out of the way entirely.
  if (rows.length === 0) return null;

  return (
    <section data-testid="my-chores-section" aria-labelledby="board-section-mine">
      <SectionLabel id="board-section-mine">{t('chores.sections.mine')}</SectionLabel>
      <div data-testid="my-chores-strip" className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
        {rows.map(renderCard)}
      </div>
    </section>
  );
}
